import React from 'react';
import {
    Grid,
    Typography,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Divider,
    Box
} from '@mui/material';
import { Field } from 'formik';

/**
 * BusinessPreferencesForm Component
 * 
 * Renders form fields for tenant-level (business) preferences.
 * These are the default values that all locations inherit from.
 * Unlike LocationPreferences, there's no inheritance toggle—these are the source defaults.
 * 
 * Organized in logical groups:
 * - Regional Settings (phone format, timezone, postal code)
 * - Business Settings (currency, date format)
 * - Document Formats (invoice, prescription number patterns)
 */

// Available options for dropdowns
const TIMEZONE_OPTIONS = [
    { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
    { value: 'America/Chicago', label: 'Central Time (Chicago)' },
    { value: 'America/Denver', label: 'Mountain Time (Denver)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
    { value: 'America/New_York', label: 'Eastern Time (New York)' },
    { value: 'UTC', label: 'UTC' }
];

const CURRENCY_OPTIONS = [
    { value: 'CAD', label: 'CAD ($)' },
    { value: 'USD', label: 'USD ($)' },
];

// const CURRENCY_POSITION_OPTIONS = [
//     { value: 'before', label: 'Before amount ($100)' },
//     { value: 'after', label: 'After amount (100$)' }
// ];

const DATE_FORMAT_OPTIONS = [
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2025-01-31)' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (01/31/2025)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/01/2025)' },
    { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY (31-01-2025)' }
];

const TIME_FORMAT_OPTIONS = [
    { value: '12', label: '12-hour (2:30 PM)' },
    { value: '24', label: '24-hour (14:30)' }
];

const POSTAL_CODE_FORMATS = [
    { value: 'A1A 1A1', label: 'A1A 1A1 (Canadian)' },
    { value: '12345', label: '12345 (US ZIP)' },
    { value: '12345-6789', label: '12345-6789 (US ZIP+4)' }
];

// const DECIMAL_PLACES_OPTIONS = [
//     { value: 0, label: '0' },
//     { value: 1, label: '1' },
//     { value: 2, label: '2' },
//     { value: 3, label: '3' }
// ];

const INVOICE_FORMAT_OPTIONS = [
    { value: 'INV-{YYYY}-{####}', label: 'INV-2025-0001 (Standard)' },
    { value: '{STORE}INV{YYYY}{####}', label: 'AINV20250001 (Compact)' },
    { value: '{YYYY}-{#####}', label: '2025-00001 (Simple)' },
    { value: 'INV-{YY}-{####}', label: 'INV-25-0001 (Short Year)' },
    { value: '{STORE}-{YYYY}-{####}', label: 'A-2025-0001 (Store Prefix)' },
    { value: 'custom', label: 'Custom Format...' }
];

// const PRESCRIPTION_FORMAT_OPTIONS = [
//     { value: 'RX-{YYYY}-{####}', label: 'RX-2025-0001 (Standard)' },
//     { value: '{STORE}RX{YYYY}{####}', label: 'ARX20250001 (Compact)' },
//     { value: 'RX{YY}{#####}', label: 'RX2500001 (Short)' },
//     { value: 'custom', label: 'Custom Format...' }
// ];

const BusinessPreferencesForm = ({ values }) => {
    const [showCustomInvoiceFormat, setShowCustomInvoiceFormat] = React.useState(false);
    const [showCustomPrescriptionFormat, setShowCustomPrescriptionFormat] = React.useState(false);

    // Check if current value is a custom format (not in predefined list)
    React.useEffect(() => {
        const invoiceFormat = values.preferences?.invoiceNumberFormat || '';
        const isCustomInvoice = invoiceFormat && !INVOICE_FORMAT_OPTIONS.some(opt => opt.value === invoiceFormat);
        setShowCustomInvoiceFormat(isCustomInvoice);

        const prescriptionFormat = values.preferences?.prescriptionNumberFormat || '';
        //const isCustomPrescription = prescriptionFormat && !PRESCRIPTION_FORMAT_OPTIONS.some(opt => opt.value === prescriptionFormat);
        //setShowCustomPrescriptionFormat(isCustomPrescription);
    }, [values.preferences?.invoiceNumberFormat, values.preferences?.prescriptionNumberFormat]);

    /**
     * Render a preference field
     */
    const renderField = (config) => {
        const {
            key,
            label,
            type = 'text',
            options = [],
            helperText,
            gridSize = 6,
            showCustom,
            setShowCustom
        } = config;

        const fieldName = `preferences.${key}`;
        const currentValue = values.preferences?.[key] || '';
        const displayValue = showCustom ? 'custom' : currentValue;

        return (
            <Grid item xs={12} sm={gridSize} key={key}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                        {label}
                    </Typography>

                    {type === 'select' ? (
                        <>
                            <Field name={fieldName}>
                                {({ field, form }) => (
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={displayValue}
                                            onChange={(e) => {
                                                const selectedValue = e.target.value;
                                                if (selectedValue === 'custom') {
                                                    setShowCustom && setShowCustom(true);
                                                } else {
                                                    setShowCustom && setShowCustom(false);
                                                    form.setFieldValue(fieldName, selectedValue);
                                                }
                                            }}
                                            displayEmpty
                                        >
                                            {options.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Field>
                            
                            {showCustom && (
                                <Field name={fieldName}>
                                    {({ field, form }) => (
                                        <TextField
                                            {...field}
                                            size="small"
                                            fullWidth
                                            placeholder="Enter custom format"
                                            sx={{ mt: 1 }}
                                            value={currentValue}
                                            onChange={(e) => form.setFieldValue(fieldName, e.target.value)}
                                        />
                                    )}
                                </Field>
                            )}
                        </>
                    ) : (
                        <Field
                            as={TextField}
                            name={fieldName}
                            size="small"
                            fullWidth
                            value={currentValue}
                        />
                    )}

                    {helperText && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                            {helperText}
                        </Typography>
                    )}
                </Box>
            </Grid>
        );
    };

    // Field configurations organized by section
    const regionalFields = [
        {
            key: 'phoneNumberFormat',
            label: 'Phone Number Format',
            helperText: 'Format for displaying phone numbers (# = digit)',
            gridSize: 6
        },
        {
            key: 'phoneNumberCountryCode',
            label: 'Country Code',
            helperText: 'Default country code for phone numbers (e.g., +1)',
            gridSize: 6
        },
        {
            key: 'timezone',
            label: 'Timezone',
            type: 'select',
            options: TIMEZONE_OPTIONS,
            helperText: 'Default timezone for all locations',
            gridSize: 6
        },
        {
            key: 'postalCodeFormat',
            label: 'Postal Code Format',
            type: 'select',
            options: POSTAL_CODE_FORMATS,
            helperText: 'Format for postal/ZIP codes',
            gridSize: 6
        }
    ];

    const businessFields = [
        {
            key: 'currency',
            label: 'Currency',
            type: 'select',
            options: CURRENCY_OPTIONS,
            helperText: 'ISO 4217 currency code',
            gridSize: 6
        },
        {
            key: 'currencySymbol',
            label: 'Currency Symbol',
            helperText: 'e.g., $, €, £',
            gridSize: 6
        },
        // {
        //     key: 'currencyPosition',
        //     label: 'Currency Position',
        //     type: 'select',
        //     options: CURRENCY_POSITION_OPTIONS,
        //     gridSize: 6
        // },
        // {
        //     key: 'decimalPlaces',
        //     label: 'Decimal Places',
        //     type: 'select',
        //     options: DECIMAL_PLACES_OPTIONS,
        //     helperText: 'Number of decimal places for currency amounts',
        //     gridSize: 6
        // },
        // {
        //     key: 'thousandsSeparator',
        //     label: 'Thousands Separator',
        //     helperText: 'Character for separating thousands (comma, period, space)',
        //     gridSize: 6
        // },
        // {
        //     key: 'decimalSeparator',
        //     label: 'Decimal Separator',
        //     helperText: 'Character for decimal point',
        //     gridSize: 6
        // },
        {
            key: 'dateFormat',
            label: 'Date Format',
            type: 'select',
            options: DATE_FORMAT_OPTIONS,
            gridSize: 6
        },
        {
            key: 'timeFormat',
            label: 'Time Format',
            type: 'select',
            options: TIME_FORMAT_OPTIONS,
            gridSize: 6
        }
    ];

    const documentFields = [
        {
            key: 'invoiceNumberFormat',
            label: 'Invoice Number Format',
            type: 'select',
            options: INVOICE_FORMAT_OPTIONS,
            helperText: 'Tokens: {YYYY}=2025, {YY}=25, {STORE}=A, {####}=0001',
            gridSize: 6,
            showCustom: showCustomInvoiceFormat,
            setShowCustom: setShowCustomInvoiceFormat
        },
        // {
        //     key: 'prescriptionNumberFormat',
        //     label: 'Prescription Number Format',
        //     type: 'select',
        //     options: PRESCRIPTION_FORMAT_OPTIONS,
        //     helperText: 'Tokens: {YYYY}=2025, {YY}=25, {STORE}=A, {####}=0001',
        //     gridSize: 6,
        //     showCustom: showCustomPrescriptionFormat,
        //     setShowCustom: setShowCustomPrescriptionFormat
        // }
    ];

    return (
        <Box sx={{ width: '100%', mt: 2 }}>
            {/* Information Box */}
            <Box sx={{
                p: 2,
                mb: 3,
                backgroundColor: 'info.lighter',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'info.light'
            }}>
                <Typography variant="caption" color="textSecondary">
                    <strong>💡 Tip:</strong> These are your business-wide defaults. All locations will inherit 
                    these settings unless they override them with location-specific preferences.
                </Typography>
            </Box>

            {/* Regional Settings */}
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                Regional Settings
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {regionalFields.map(renderField)}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Business Settings */}
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                Business Settings
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {businessFields.map(renderField)}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Document Formats */}
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                Document Formats
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {documentFields.map(renderField)}
            </Grid>

        </Box>
    );
};

export default BusinessPreferencesForm;
